import { CORE_SIZE__SINGLEMODE, EQUIPMENT_TYPE__PT1, EQUIPMENT_TYPE__RL1, MTJ__DOUBLE, MTJ__SINGLE_END_A, MTJ__SINGLE_END_A_THEN_END_B, MTJ__SINGLE_END_B, REAL_TIME_DISPLAY__FAST, REAL_TIME_DISPLAY__FULL, TEST_PLAN_TEMPLATE__FAN_OUT, TEST_PLAN_TEMPLATES } from "@shared/constants/constants";
import { Equipment, FiberMapEntry, RequiredValues, PolarityTestblockProcedureBase, TestBlockProcedureBase, CreateTestPlanDTO, TestPlanTemplates, WizardData, WizardState } from "@shared/types/test-plan/types.d";

/**
 * Creates a cable map (ex. {"1a": "1a-12a", "1b": "1b-12b"} or {"1a": "1a", "2a": "2a", ...} )
 * @param testPlanTemplate - @type {TestPlanTemplates}
 * @param fibersPerCable - @type {number}
 * @param cablesPerAssembly - @type {number}
 * @param equipment - @type {Equipment}
 * @returns void
 */
function getCableMap(testPlanTemplate: TestPlanTemplates, fibersPerCable: number, cablesPerAssembly: number, equipment: Equipment) {
    let cableMap_1 = {};
    if (equipment.mtjConfiguration) {
        let breakCableMap = Object.entries(equipment.breakCable);

        for (let i = 0; i < breakCableMap.length; i++) {
            if (breakCableMap[i][1] === true) {
                let segment = breakCableMap[i][0];
                let fiberName = breakCableMap[i][0];

                if (fibersPerCable !== 1) {
                    if (
                        testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT &&
                        breakCableMap[i][0].includes('b') &&
                        equipment.mtjConfiguration !== MTJ__DOUBLE &&
                        breakCableMap[i][1] === true
                    ) {
                    } else {
                        if (i !== breakCableMap.length - 1) {
                            while (breakCableMap[i + 1][1] !== true) {
                                i++;
                                if (i === breakCableMap.length - 1 || breakCableMap[i + 1][1] === true) {
                                    segment = segment + '-' + breakCableMap[i][0];
                                    break;
                                }
                            }
                        }
                    }
                }
                cableMap_1[fiberName] = segment;
            }
        }
    }

    if (Object.keys(cableMap_1).length === 0) {
        // Prepare cable map
        if (equipment.mtjConfiguration === MTJ__DOUBLE) {
            for (let i = 0; i < cablesPerAssembly; i++) {
                let firstFiber = 1 + fibersPerCable * i + 'a/b';
                let lastFiber = fibersPerCable * (i + 1) + 'a/b';

                cableMap_1[firstFiber] = fibersPerCable === 1 ? firstFiber : `${firstFiber}-${lastFiber}`;
            }
        } else {
            // Cable map end A
            for (let i = 0; i < cablesPerAssembly; i++) {
                let firstFiber = 1 + fibersPerCable * i + 'a';
                let lastFiber = fibersPerCable * (i + 1) + 'a';

                cableMap_1[firstFiber] = fibersPerCable === 1 ? firstFiber : `${firstFiber}-${lastFiber}`;
            }

            // Cable map end B
            if (testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                let totalFibers = fibersPerCable * cablesPerAssembly;
                for (let i = 0; i < totalFibers; i++) {
                    let firstFiber = 1 + fibersPerCable * i + 'b';

                    cableMap_1[firstFiber] = firstFiber;
                }
            } else {
                for (let i = 0; i < cablesPerAssembly; i++) {
                    let firstFiber = 1 + fibersPerCable * i + 'b';
                    let lastFiber = fibersPerCable * (i + 1) + 'b';

                    cableMap_1[firstFiber] = fibersPerCable === 1 ? firstFiber : `${firstFiber}-${lastFiber}`;
                }
            }
        }
    }

    return cableMap_1;
}

function generatePolarityTestBlockBase(testPlan: CreateTestPlanDTO, polarityMapping: WizardData['assembly']['polarity']['mapping']) {
    let equipment: PolarityTestblockProcedureBase = {
        type: EQUIPMENT_TYPE__PT1,
        polarity: polarityMapping
    };

    testPlan.equipment.push(equipment);
}

function generateTestBlockBase(data: WizardData, required: RequiredValues, wavelengths: number[], testPlan: CreateTestPlanDTO) {
    let fiberMappingTestBlockSideA = {};
    let fiberMappingTestBlockSideB = {};

    let referenceBlockA: { referenceMap: Record<string, FiberMapEntry>; referencePauseBefore: string[]; } = {
        referenceMap: {},
        referencePauseBefore: []
    };
    let referenceBlockB: { referenceMap: Record<string, FiberMapEntry>; referencePauseBefore: string[]; } = {
        referenceMap: {},
        referencePauseBefore: []
    };

    // The fiber map is twice as long if both sides of the assembly must be tested
    let fibersPerSide = data.fibersPerCable * data.cablesPerAssembly;
    let mapLength = data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B ? fibersPerSide * 2 : fibersPerSide;

    // Create fiber maps
    generateFiberMap(data, required, mapLength, fibersPerSide, fiberMappingTestBlockSideB, fiberMappingTestBlockSideA);

    // Create reference map
    generateReferenceMap(data, referenceBlockB, referenceBlockA);

    let testBlockName;
    if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
        data.equipment.singleTestBlock) {
        testBlockName = 'IL/RL End A then End B';
    } else if (data.equipment.mtjConfiguration === MTJ__DOUBLE) {
        testBlockName = 'IL/RL End A and End B';
    } else if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_B) {
        testBlockName = 'IL/RL End B Only';
    } else if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A) {
        testBlockName = 'IL/RL End A Only';
    } else {
        testBlockName = 'IL/RL End A';
    }

    let realTimeDisplayWavelengths = getRealTimeDisplayWavelengths(wavelengths, data.realTimeDisplaySelection);

    // Base equipment structure
    let equipmentTestBlockSideA: TestBlockProcedureBase = {
        testBlockName: testBlockName,
        type: EQUIPMENT_TYPE__RL1,
        tests: {},
        realTimeDisplay: {
            measurements: [],
            wl: realTimeDisplayWavelengths
        },
        testPlanDisplay: {
            measurements: [],
            wl: wavelengths
        },
        showDutLength: data.showDutLength,
        results: data.results,
        allowableRetryCount: data.allowableRetryCount,
        retryFailedChannels: data.retryFailedChannels,
        //'fastFail' : null,
        fastFail: data.equipment.fastFail,
        ilResolution: data.equipment.ilResolution,
        autoStart: {
            wl: wavelengths[0]
        },
        rlMode: data.equipment.rlMode,
        pauseBefore: [],
        pauseMessages: [],
        referencePauseBefore: [],
        referencePauseMessages: [],
        fiberMap: {},
        referenceMap: {},
        insertionLossTest: false,
        returnLossTest: false,
        tj1: data.equipment.tj1,
        tj2: data.equipment.tj2
    };

    // Deep copy
    let equipmentTestBlockSideB = JSON.parse(JSON.stringify(equipmentTestBlockSideA));
    equipmentTestBlockSideB.testBlockName = 'IL/RL End B';

    // Set autoStart fiber
    equipmentTestBlockSideA.autoStart.fiber = data.equipment.fibers[data.fibersPerCable - 1];
    if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
        !data.equipment.singleTestBlock) {
        equipmentTestBlockSideB.autoStart.fiber =
            data.equipment.fibers[fibersPerSide + data.fibersPerCable - 1];
    }

    // Set fiberMap
    equipmentTestBlockSideA.fiberMap = fiberMappingTestBlockSideA;
    equipmentTestBlockSideB.fiberMap = fiberMappingTestBlockSideB;

    // Set pauseMessages
    equipmentTestBlockSideA.pauseMessages = data.equipment.pauseMessages;
    equipmentTestBlockSideB.pauseMessages = data.equipment.pauseMessages;

    // Set referenceMap
    equipmentTestBlockSideA.referenceMap = referenceBlockA.referenceMap;
    equipmentTestBlockSideB.referenceMap = referenceBlockB.referenceMap;

    // Set referencePauseBefore
    equipmentTestBlockSideA.referencePauseBefore = referenceBlockA.referencePauseBefore;
    equipmentTestBlockSideB.referencePauseBefore = referenceBlockB.referencePauseBefore;

    // Set referencePauseMessages
    equipmentTestBlockSideA.referencePauseMessages = data.equipment.reference.pauseMessages;
    equipmentTestBlockSideB.referencePauseMessages = data.equipment.reference.pauseMessages;

    // Set insertionLossTest & returnLossTest
    if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
        !data.equipment.singleTestBlock) {
        // End A
        equipmentTestBlockSideA.insertionLossTest = data.assembly.insertionLossSideA;
        equipmentTestBlockSideA.returnLossTest = data.assembly.returnLossSideA || data.assembly.returnLossTotal;

        equipmentTestBlockSideA.tests.ilA = data.assembly.insertionLossSideA;
        equipmentTestBlockSideA.tests.ilB = false;
        equipmentTestBlockSideA.tests.rlA = data.assembly.returnLossSideA;
        equipmentTestBlockSideA.tests.rlB = false;
        equipmentTestBlockSideA.tests.rlTotal = data.assembly.returnLossTotal;
        equipmentTestBlockSideA.tests.length = data.showDutLength;

        // End B
        equipmentTestBlockSideB.insertionLossTest = data.assembly.insertionLossSideB;
        equipmentTestBlockSideB.returnLossTest = data.assembly.returnLossSideB || data.assembly.returnLossTotal;

        equipmentTestBlockSideB.tests.ilA = false;
        equipmentTestBlockSideB.tests.ilB = data.assembly.insertionLossSideB;
        equipmentTestBlockSideB.tests.rlA = false;
        equipmentTestBlockSideB.tests.rlB = data.assembly.returnLossSideB;
        equipmentTestBlockSideB.tests.rlTotal = data.assembly.returnLossTotal;
        equipmentTestBlockSideB.tests.length = data.showDutLength;
    } else {
        equipmentTestBlockSideA.insertionLossTest =
            data.assembly.insertionLossSideA ||
            data.assembly.insertionLossSideB ||
            data.assembly.insertionLossTotal;

        equipmentTestBlockSideA.returnLossTest =
            data.assembly.returnLossSideA ||
            data.assembly.returnLossSideB ||
            data.assembly.returnLossTotal;

        if (data.equipment.mtjConfiguration === MTJ__DOUBLE) {
            equipmentTestBlockSideA.tests.ilA = true;
            equipmentTestBlockSideA.tests.ilB = true;
            equipmentTestBlockSideA.tests.rlA = data.assembly.returnLossSideA;
            equipmentTestBlockSideA.tests.rlB = data.assembly.returnLossSideB;
            equipmentTestBlockSideA.tests.rlTotal = data.assembly.returnLossTotal;
            equipmentTestBlockSideA.tests.length = data.showDutLength;
        } else {
            equipmentTestBlockSideA.tests.ilA = data.assembly.insertionLossSideA;
            equipmentTestBlockSideA.tests.ilB = data.assembly.insertionLossSideB;
            equipmentTestBlockSideA.tests.rlA = data.assembly.returnLossSideA;
            equipmentTestBlockSideA.tests.rlB = data.assembly.returnLossSideB;
            equipmentTestBlockSideA.tests.rlTotal = data.assembly.returnLossTotal;
            equipmentTestBlockSideA.tests.length = data.showDutLength;
        }
    }

    // Set pauseBefore
    data.equipment.pauseBefore.forEach((pauseBefore, i) => {
        if (pauseBefore) {
            if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                !data.equipment.singleTestBlock &&
                i >= fibersPerSide) {
                equipmentTestBlockSideB.pauseBefore.push(data.equipment.fibers[i]);
            } else {
                equipmentTestBlockSideA.pauseBefore.push(data.equipment.fibers[i]);
            }
        }
    });

    // Set realTimeDisplay & testPlanDisplay
    if (data.assembly.insertionLossSideA ||
        data.assembly.insertionLossSideB ||
        data.assembly.insertionLossTotal) {
        let measurementsBlockA = {};
        let measurementsBlockB = {};

        for (let i = 0; i < mapLength; i++) {
            if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                !data.equipment.singleTestBlock &&
                i >= fibersPerSide) {
                measurementsBlockB['il' + data.equipment.detectors[i]] = true;
            } else {
                measurementsBlockA['il' + data.equipment.detectors[i]] = true;
            }
        }

        equipmentTestBlockSideA.realTimeDisplay.measurements = Object.keys(measurementsBlockA);
        equipmentTestBlockSideA.testPlanDisplay.measurements = Object.keys(measurementsBlockA);

        equipmentTestBlockSideB.realTimeDisplay.measurements = Object.keys(measurementsBlockB);
        equipmentTestBlockSideB.testPlanDisplay.measurements = Object.keys(measurementsBlockB);
    }

    if (data.assembly.returnLossSideA) {
        equipmentTestBlockSideA.testPlanDisplay.measurements.push('rla');
    }

    if (data.assembly.returnLossSideB) {
        equipmentTestBlockSideB.testPlanDisplay.measurements.push('rlb');
    }

    if (data.assembly.returnLossTotal) {
        equipmentTestBlockSideA.testPlanDisplay.measurements.push('rltotal');
        equipmentTestBlockSideB.testPlanDisplay.measurements.push('rltotal');
    }

    if (data.realTimeDisplaySelection === REAL_TIME_DISPLAY__FULL) {
        equipmentTestBlockSideA.realTimeDisplay.measurements = [...equipmentTestBlockSideA.testPlanDisplay.measurements];
        equipmentTestBlockSideB.realTimeDisplay.measurements = [...equipmentTestBlockSideB.testPlanDisplay.measurements];
    }

    if (equipmentTestBlockSideA.returnLossTest) {
        equipmentTestBlockSideA.tj1 = data.equipment.tj1;

        if (data.equipment.mtjConfiguration === MTJ__DOUBLE) {
            equipmentTestBlockSideA.tj2 = data.equipment.tj2;
        }
    }

    if (equipmentTestBlockSideB.returnLossTest) {
        equipmentTestBlockSideB.tj1 = data.equipment.tj1;

        if (data.equipment.mtjConfiguration === MTJ__DOUBLE) {
            equipmentTestBlockSideB.tj2 = data.equipment.tj2;
        }
    }

    testPlan.equipment.push(equipmentTestBlockSideA);

    if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
        !data.equipment.singleTestBlock) {
        testPlan.equipment.push(equipmentTestBlockSideB);
    }
}

function getRealTimeDisplayWavelengths(wavelengths: number[], realTimeDisplaySelection: WizardData['realTimeDisplaySelection']) {
    let tempWl = wavelengths;
    if (realTimeDisplaySelection === REAL_TIME_DISPLAY__FAST && wavelengths.length !== 2) {
        if (wavelengths.length === 4 || wavelengths.length === 3) {
            tempWl = [wavelengths[0], wavelengths[2]];
        }
    }
    return tempWl;
}

function generateReferenceMap(state: WizardData, block2: { referenceMap: FiberMapEntry; referencePauseBefore: string[]; }, block1: { referenceMap: FiberMapEntry; referencePauseBefore: string[]; }) {
    for (let i = 0; i < state.equipment.reference.fibers.length; i++) {
        let map: FiberMapEntry = {
            skip: false
        };

        if (state.equipment.reference.skip[i]) {
            map.skip = true;
        } else {
            //
            // Create map data structure
            //
            map.switches = [];
            map.detector = state.equipment.reference.detectors[i];
            //
            // Switch positions
            //
            // Switch 0 (Output/Internal Switch)
            if (state.equipment.reference.switch0[i] !== null) {
                map.switches.push({
                    sw: 0,
                    ch: state.equipment.reference.switch0[i]
                });
            }
            // Switch 1
            if (state.equipment.useSwitch1 && state.equipment.reference.switch1[i] !== null) {
                map.switches.push({
                    sw: 1,
                    ch: state.equipment.reference.switch1[i]
                });
            }

            // Switch 2
            if (state.equipment.useSwitch2 && state.equipment.reference.switch2[i] !== null) {
                map.switches.push({
                    sw: 2,
                    ch: state.equipment.reference.switch2[i]
                });
            }
        }

        // If ends A and B are tested in separate test blocks
        if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
            !state.equipment.singleTestBlock) {
            if (state.hybridAssembly) {
                // if in second half (so if in B end)
                if (i >= state.equipment.reference.fibers.length / 2) {
                    block2.referenceMap[state.equipment.reference.fibers[i]] = map;
                    if (state.equipment.reference.pauseBefore[i]) {
                        block2.referencePauseBefore.push(state.equipment.reference.fibers[i]);
                    }
                } else {
                    // first half (A end)
                    block1.referenceMap[state.equipment.reference.fibers[i]] = map;
                    if (state.equipment.reference.pauseBefore[i]) {
                        block1.referencePauseBefore.push(state.equipment.reference.fibers[i]);
                    }
                }
            } else {
                block1.referenceMap[state.equipment.reference.fibers[i]] = map;
                block2.referenceMap[state.equipment.reference.fibers[i]] = map;
                if (state.equipment.reference.pauseBefore[i]) {
                    block1.referencePauseBefore.push(state.equipment.reference.fibers[i]);
                    block2.referencePauseBefore.push(state.equipment.reference.fibers[i]);
                }
            }
        } else {
            block1.referenceMap[state.equipment.reference.fibers[i]] = map;
            if (state.equipment.reference.pauseBefore[i]) {
                block1.referencePauseBefore.push(state.equipment.reference.fibers[i]);
            }
        }
    }
}

function generateFiberMap(data: WizardData, required: RequiredValues, mapLength: number, fibersPerSide: number, fiberMappingTestBlock2: {}, fiberMappingTestBlock1: {}) {
    for (let i = 0; i < mapLength; i++) {
        //
        // Skip this fiber?
        //
        let map: FiberMapEntry = {
            skip: false
        };

        //
        // Create map data structure
        //
        map.switches = [];
        map.detector = data.equipment.detectors[i];
        //
        // Switch positions
        //
        // Switch 0 (Output/Internal Switch)
        if (data.equipment.switch0[i] !== null) {
            map.switches.push({
                sw: 0,
                ch: data.equipment.switch0[i]
            });
        }
        // Switch 1
        if (data.equipment.useSwitch1 && data.equipment.switch1[i] !== null) {
            map.switches.push({
                sw: 1,
                ch: data.equipment.switch1[i]
            });
        }
        // Switch 2
        if (data.equipment.useSwitch2 && data.equipment.switch2[i] !== null) {
            map.switches.push({
                sw: 2,
                ch: data.equipment.switch2[i]
            });
        }
        //
        // IL/RL Limits
        //
        // If only end A is tested
        if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A) {
            // If ILA is selected
            if (data.assembly.insertionLossSideA) {
                map.ilLimit = data.assembly.sideA.il[i];
            }
            // If RLA is selected
            if (data.assembly.returnLossSideA) {
                map.rlLimitA = data.assembly.sideA.rl[i];
            }
            // If RLTotal is selected
            if (data.assembly.returnLossTotal) {
                map.rlLimitTotal = data.assembly.sideA.rlTotal[i];
            }
        }

        // If only end B is tested
        else if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_B) {
            // If ILB is selected
            if (data.assembly.insertionLossSideB) {
                map.ilLimit = data.assembly.sideB.il[i];
            }
            // If RLB is selected
            if (data.assembly.returnLossSideB) {
                map.rlLimitA = data.assembly.sideB.rl[i];
            }
            // If RLTotal is selected
            if (data.assembly.returnLossTotal) {
                map.rlLimitTotal = data.assembly.sideB.rlTotal[i];
            }
        }

        // If both ends are tested
        else if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            // If ILA is selected
            if (data.assembly.insertionLossSideA && i < fibersPerSide) {
                map.ilLimit = data.assembly.sideA.il[i];
            }
            // If ILB is selected
            if (data.assembly.insertionLossSideB && i >= fibersPerSide) {
                map.ilLimit = data.assembly.sideB.il[i - fibersPerSide];
            }
            // If RLA is selected
            if (data.assembly.returnLossSideA && i < fibersPerSide) {
                map.rlLimitA = data.assembly.sideA.rl[i];
            }
            // If RLB is selected
            if (data.assembly.returnLossSideB && i >= fibersPerSide) {
                map.rlLimitA = data.assembly.sideB.rl[i - fibersPerSide];
            }
            // If RLTotal is selected
            if (data.assembly.returnLossTotal && i < fibersPerSide) {
                map.rlLimitTotal = data.assembly.sideA.rlTotal[i];
            }
            if (data.assembly.returnLossTotal && i >= fibersPerSide) {
                map.rlLimitTotal = data.assembly.sideB.rlTotal[i - fibersPerSide];
            }
        }

        // If both ends are tested in dual mode
        else if (data.equipment.mtjConfiguration === MTJ__DOUBLE) {
            // In this mode, IL = ILA + ILB
            // Therefore IL Side A and IL Side B must both be checked or unchecked
            // if (state.assembly.insertionLossSideA && state.assembly.insertionLossSideB )
            if (data.assembly.insertionLossTotal) {
                //map.ilLimit = state.assembly.sideA.il[i] + state.assembly.sideB.il[i];
                map.ilLimit = data.assembly.ilTotalList[i];
            }
            // If RLA is selected
            if (data.assembly.returnLossSideA) {
                map.rlLimitA = data.assembly.sideA.rl[i];
            }
            // If RLB is selected
            if (data.assembly.returnLossSideB) {
                map.rlLimitB = data.assembly.sideB.rl[i];
            }
            // If RLTotal is selected
            if (data.assembly.returnLossTotal) {
                map.rlLimitTotal = data.assembly.sideA.rlTotal[i];
            }
        }
        //
        // TJ lengths
        //
        // TJ1 Length
        if (required.tj1Length && data.equipment.tj1Length !== null) {
            map.TJaLen = data.equipment.tj1Length[i];
        }
        // TJ2 Length
        if (required.tj2Length && data.equipment.tj2Length !== null) {
            map.TJbLen = data.equipment.tj2Length[i];
        }

        // If ends A and B are tested in separate test blocks, the second half of fibers must be kept separate
        if (data.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
            !data.equipment.singleTestBlock &&
            i >= fibersPerSide) {
            fiberMappingTestBlock2[data.equipment.fibers[i]] = map;
        } else {
            fiberMappingTestBlock1[data.equipment.fibers[i]] = map;
        }
    }
}

function getValidWavelengths(wizardData: WizardData) {
    let wavelengths: number[] = [];
    let validWavelengths = wizardData.assembly.coreSize === CORE_SIZE__SINGLEMODE ? [1310, 1490, 1550, 1625] : [850, 1300];
    for (let wavelength of validWavelengths) {
        if (wizardData['wavelength_' + wavelength]) {
            wavelengths.push(wavelength);
        }
    }
    return wavelengths;
}

export async function buildTestPlanDTO(name: string, testPlanTemplate: TestPlanTemplates, wizardData: WizardData, required: RequiredValues): Promise<CreateTestPlanDTO> {
    let testPlanTemplateId = TEST_PLAN_TEMPLATES.find(
        (template) => template.name === testPlanTemplate
    )?.id ?? null;

    let wavelengths: number[] = getValidWavelengths(wizardData);

    let cableMap_1 = getCableMap(
        testPlanTemplate,
        wizardData.fibersPerCable,
        wizardData.cablesPerAssembly,
        wizardData.equipment
    );

    let testPlan: CreateTestPlanDTO = {
        wizardData: wizardData,
        name: name,
        testPlanTemplateId: testPlanTemplateId,
        label: wizardData.label,
        customFields: wizardData.customFields,
        assembly: {
            coreSize: wizardData.assembly.coreSize,
            wavelengths: wavelengths,
            assemblyType: wizardData.assemblyType,
            hybridAssembly: wizardData.hybridAssembly,
            assemblyLength: wizardData.assemblyLength,
            fibersPerCable: wizardData.fibersPerCable,
            cablesPerAssembly: wizardData.cablesPerAssembly,
            cableMap_1,
            ilMinimumLimit: wizardData.assembly.ilMinimumLimit
        },
        equipment: [],
        serialNumScheme: wizardData.serialNumScheme,
        autoPrintBarcode: wizardData.autoPrintBarcode,
        autoIncrement: wizardData.autoIncrement,
        cableAssemblyExportResult: wizardData.cableAssemblyExportResult
    };

    // Create fiber map(s)
    // Note: The second fiber map is only used with the MTJ__SINGLE_END_A_THEN_END_B configuration when AA...BB is selected.
    generateTestBlockBase(wizardData, required, wavelengths, testPlan);

    //
    // PT1
    //
    if (wizardData.assembly.testPolarity) {
        generatePolarityTestBlockBase(testPlan, wizardData.assembly.polarity.mapping);
    }
    
    return testPlan;
}

export function createWizardData(state: WizardState): WizardData {
    let filtered = state.customFields;

    if (!state.ilComp.userInput && state.customFields.some((e) => e.name === 'Length')) {
        filtered = state.customFields.filter(function (value, index, arr) {
            return value.name !== 'Length';
        });
    }

    let wizardData: WizardData = {
            version: 7,

            assembly: state.assembly,
            wavelength_850: state.wavelength_850,
            wavelength_1300: state.wavelength_1300,
            wavelength_1310: state.wavelength_1310,
            wavelength_1490: state.wavelength_1490,
            wavelength_1550: state.wavelength_1550,
            wavelength_1625: state.wavelength_1625,
            assemblyType: state.assemblyType,
            hybridAssembly: state.hybridAssembly,
            assemblyLength: state.assemblyLength,
            fibersPerCable: state.fibersPerCable,
            cablesPerAssembly: state.cablesPerAssembly,

            equipment: state.equipment,

            realTimeDisplaySelection: state.realTimeDisplaySelection,
            results: state.results,
            allowableRetryCount: state.allowableRetryCount,
            retryFailedChannels: state.retryFailedChannels,
            showDutLength: state.showDutLength,

            serialNumScheme: state.serialNumScheme,
            autoPrintBarcode: state.autoPrintBarcode,
            trackDUTCount: state.trackDUTCount,

            autoIncrement: state.autoIncrement,
            cableAssemblyExportResult: state.cableAssemblyExportResult,

            partialResults: state.partialResults,

            customFields: filtered,
            label: state.label,
            excelLabel: state.excelLabel,
            secondExcelLabel: state.secondExcelLabel,

            additionalSpecs: state.additionalSpecs,
            ilComp: state.ilComp
    }

    return wizardData;
}